const normalize = (type) => String(type || '').trim().toLowerCase();

/**
 * Splits an attack's damage across its damage types, so resistance, vulnerability and immunity apply to each type on
 * its own (2024: "1d10 piercing + 2d6 poison" against a raging Barbarian halves only the piercing).
 *
 * `parts` are the rolled pieces as `{ type, value }`; a piece without a type (such as Rage Damage) is the attack's own
 * type, `fallbackType`, or else the first typed piece's. `total` is the damage to divide (after any reduction a reaction
 * made, such as Uncanny Dodge): when it differs from what the pieces add up to, each type gets its proportional share,
 * rounded so the shares still add up to `total`. Returns `[{ type, before }]` with one entry per type.
 */
export const damageByType = (parts = [], total, fallbackType = '') => {
  const amount = Math.max(0, Math.floor(Number(total) || 0));
  const inherited = normalize(fallbackType) || normalize((parts || []).find((part) => normalize(part?.type))?.type);
  const rolled = new Map();
  for (const part of parts || []) {
    const value = Math.max(0, Math.floor(Number(part?.value) || 0));
    const type = normalize(part?.type) || inherited;
    if (value > 0 && type) rolled.set(type, (rolled.get(type) || 0) + value);
  }
  const entries = [...rolled];
  if (entries.length <= 1) return [{ type: entries[0]?.[0] || inherited, before: amount }];
  const sum = entries.reduce((total, [, value]) => total + value, 0);
  if (sum === amount) return entries.map(([type, before]) => ({ type, before }));
  const shares = entries.map(([type, value]) => ({ type, exact: (value * amount) / sum }));
  const result = shares.map(({ type, exact }) => ({ type, before: Math.floor(exact) }));
  let left = amount - result.reduce((total, entry) => total + entry.before, 0);
  [...shares.keys()].sort((a, b) => (shares[b].exact % 1) - (shares[a].exact % 1) || a - b)
    .forEach((index) => { if (left > 0) { result[index].before += 1; left -= 1; } });
  return result;
};
